/**
 * 小助理 ·「查看实绩」弹窗（纯只读汇总，⛔ 不改任何存档字段）。
 *
 * 两区：
 *  - **奖项**：读 `player.awardHistory`，只留玩家沾边的记录（获奖 = 得主是她，入围 = 提名未得）
 *  - **作品**：读 `player.filmography`（= 播出已完结的剧），新作在上
 *
 * 数据口径与 AwardSystem 同源，⛔ 另起一套算法：
 *  - 剧名 / 角色定位读履历**快照**（剧本表改字不回写历史）；角色名⛔ 不入快照 ⇒ 回 `roles[roleIndex].name`
 *  - 热度 = `fanSystem.workHeatOf(script)`（含剧宣与客串累计、各自封顶），与当年评选名单读的是同一个数
 *  - 「爆」标读**履历快照** `f.boom`（入档时按 `workHeat ≥ HEAT_BOOM_LINE` 定格，见 docs/实现说明.md 迁存章 A4 节三），
 *    与热度榜/结算履历同一枚 `.heatchart-boom`；⛔ 回表现算——完结后热度会走样、剧下架后更是查无此数
 *  - 剧本已从表中缺失（删表/坏档）⇒ 角色名与热度落空，仍按快照把剧名/定位/评分显示出来
 *
 * 后续要加的实绩（商务代言等）在 `render()` 里再 append 一区即可，⛔ 把「两区」写死进渲染流程。
 */
import { GameState } from '../../systems/GameState';
import { GameConfig } from '../../core/GameConfig';
import { GameData } from '../../data/GameData';
import { fanSystem } from '../../systems/FanSystem';
import { AWARD_MAP, absMonthOf, AwardId, findCeremony } from '../../data/tables/Awards';
import { AwardRecord, FilmographyEntry, ScriptGenre } from '../../data/types';
import { createDiv, createModalDismiss, createSpan } from '../ui';

/** 剧本类型中文名（与月度执行页剧本详情同一套叫法，那边是面板内的局部函数，⛔ 为一个小映射去改它） */
const GENRE_CN: Record<ScriptGenre, string> = {
    movie: '电影', tv: '电视剧', variety: '综艺', web: '网剧', short: '短剧', other: '其他',
};

export class CareerRecordPanel {
    readonly element: HTMLElement;
    private readonly sectionsBox: HTMLElement;
    /** 弹窗开合时通知宿主（悬浮入口要让位，与 VN 纸卡同一条规矩） */
    onOpenChanged: ((open: boolean) => void) | null = null;

    constructor() {
        const wrap = createDiv('modal-wrap career-pop');
        const card = createDiv('modal-note-card career-card');
        card.appendChild(createDiv('mtitle', ['我的实绩']));
        this.sectionsBox = createDiv('career-body');
        card.appendChild(this.sectionsBox);
        // 2026-10-16 弹窗统一批（茄子令）：卡内那颗「关闭」删掉，关闭热区＝遮罩本体（见 ui.ts createModalDismiss）
        wrap.appendChild(createModalDismiss(() => this.close()));
        wrap.appendChild(card);

        this.element = wrap;
    }

    get isOpen(): boolean {
        return this.element.classList.contains('show');
    }

    /** 每次打开重算：实绩是活账本，⛔ 缓存（上次开到现在可能又播完一部戏、颁完一届奖） */
    open(): void {
        if (this.isOpen) return;
        this.render();
        this.element.classList.add('show');
        this.onOpenChanged?.(true);
    }

    close(): void {
        if (!this.isOpen) return;
        this.element.classList.remove('show');
        this.onOpenChanged?.(false);
    }

    private render(): void {
        const p = GameState.getInstance().player;
        this.sectionsBox.innerHTML = '';
        if (!p) return;   // 无档可查（理论到不了：入口只在游戏会话内可点）
        this.sectionsBox.appendChild(awardSection(p.awardHistory ?? []));
        this.sectionsBox.appendChild(workSection(p.filmography ?? []));
        this.sectionsBox.appendChild(endorseSection(p));
    }
}

// ===== 两区构造（纯函数，不读单例的运行时态，便于日后挪位） =====

/** 代言区（商务代言批 2026-10-30，收实现说明 §3.4f 待办）：累计读数+全部在约/已结束合约，新的在前 */
function endorseSection(p: import('../../data/types').PlayerState): HTMLElement {
    const list = p.endorsements ?? [];
    if (!list.length) return section('代言', [], '还没有商务合作入账，报价单来了我叫你。');   // 空态优先，⛔ 空列表还挂「累计 0 次」行
    const rows = [...list]
        .sort((a, b) => b.signedAbs - a.signedAbs)
        .map(c => {
            const brand = GameData.BRAND_MAP[c.brandId];
            const item = createDiv('career-item');
            item.appendChild(createDiv('career-line', [
                createSpan('career-title', brand?.name ?? c.brandId),
                createSpan('career-sub', c.title === 'friend' ? '品牌挚友 · 单次活动' : c.title === 'ambassador' ? '品牌大使' : '品牌代言人'),
                c.active ? createSpan('career-chip', '在约') : createSpan('career-flag', '已结束'),
            ]));
            item.appendChild(createDiv('career-line', [
                createSpan('career-sub', c.title === 'friend'
                    ? `品牌活动 · 出席当天结 ¥${((c.eventFee ?? 0)).toLocaleString()}`
                    : `长约 5:5 · 尾款 ¥${Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE / 2).toLocaleString()} 约满结`),
            ]));
            return item;
        });
    const head = createDiv('career-item');
    head.appendChild(createDiv('career-line', [
        createSpan('career-title', `累计签约 ${p.endorseTotal ?? 0} 次`),
        createSpan('career-nums', `¥${(p.endorseEarned ?? 0).toLocaleString()}`),
    ]));
    rows.unshift(head);
    return section('代言', rows, '还没有商务合作入账，报价单来了我叫你。');
}

/** 奖项区：获奖在前、入围在后，各自按颁奖时间倒序（玩家更想先看拿到手的那几座） */
function awardSection(history: AwardRecord[]): HTMLElement {
    const mine = history.filter(r => !r.scripted && (r.playerWon || r.playerNominated));
    const byTimeDesc = (a: AwardRecord, b: AwardRecord) =>
        ceremonyAbs(b) - ceremonyAbs(a) || b.edition - a.edition;
    const rows = [
        ...mine.filter(r => r.playerWon).sort(byTimeDesc),
        ...mine.filter(r => !r.playerWon).sort(byTimeDesc),
    ].map(recordRow);
    return section('奖项', rows, '还没有奖项入账，等提名下来我叫你。');
}

/** 作品区：只收播出完结的剧（拍摄中/播出中⛔ 上账——评分与口碑都还没定稿） */
function workSection(entries: FilmographyEntry[]): HTMLElement {
    const rows = [...entries]
        .sort((a, b) => absMonthOf(b.year, b.month) - absMonthOf(a.year, a.month))
        .map(filmRow);
    return section('作品', rows, '还没有播完的戏，杀青了我记一笔。');
}

/** 颁奖届的绝对月份：查得到定义按颁奖月算，查不到（届表改过/老档）退化成记录年份的 1 月 */
function ceremonyAbs(r: AwardRecord): number {
    const def = findCeremony(r.awardId as AwardId, r.edition);
    return def ? absMonthOf(def.yearIndex, def.month) : absMonthOf(r.year, 1);
}

/** 一条奖项记录 → 一行：届别奖项名 + 获奖/入围小旗，次行作品与角色，入围再补一句本届得主 */
function recordRow(r: AwardRecord): HTMLElement {
    const award = AWARD_MAP[r.awardId as AwardId];
    const me = r.nominees.find(n => n.performer === 'player');
    const item = createDiv(`career-item ${r.playerWon ? 'win' : 'nom'}`);
    item.appendChild(createDiv('career-line', [
        createSpan('career-title',
            `第${r.edition} 届 ${award?.name ?? r.awardId}${award?.categoryNames[r.category] ?? r.category}`),
        flag(r.playerWon ? '获奖' : '入围', r.playerWon),
    ]));
    if (me) {
        item.appendChild(createDiv('career-line', [
            createSpan('career-sub', workLabel(me.workName, roleLabelOf(me.scriptId, me.roleIndex, me.roleType, me.roleName))),
        ]));
    }
    const winner = r.nominees[0];
    if (!r.playerWon && winner) {
        item.appendChild(createDiv('career-note', [`本届得主：${winner.name}《${winner.workName}》`]));
    }
    return item;
}

/** 一条履历 → 一行：剧名 + 类型 +（爆）+ 评分/热度，次行定位「角色」+ 完结时间 */
function filmRow(f: FilmographyEntry): HTMLElement {
    const script = GameData.SCRIPT_MAP[f.scriptId];
    const item = createDiv('career-item');
    const line1: (HTMLElement | string)[] = [
        createSpan('career-title', `《${f.workName || script?.name || '未知作品'}》`),
        chip(GENRE_CN[f.genre] ?? f.genre),
    ];
    // 「爆」读入档定格快照（与热度榜/结算履历同口径：workHeat ≥ HEAT_BOOM_LINE），⛔ 回表现算——完结后热度会走样
    if (f.boom) line1.push(createSpan('heatchart-boom', '爆'));
    line1.push(createSpan('career-nums',
        `评分 ${f.score.toFixed(1)}${script ? ` · 热度 ${fanSystem.workHeatOf(script)}` : ''}`));
    item.appendChild(createDiv('career-line', line1));
    item.appendChild(createDiv('career-line', [
        createSpan('career-sub', roleLabelOf(f.scriptId, f.roleIndex, f.roleType)),
        createSpan('career-when', `第${f.year + 1}年·${f.month}月`),
    ]));
    return item;
}

/** 定位「角色名」；表里查不到该位时只留定位（快照仍在，⛔ 编一个角色名） */
function roleLabelOf(scriptId: string, roleIndex: number, roleType: string, roleName?: string): string {
    // 固定阵容（roleIndex = -1）没有 roles 位，角色名在提名快照里（2026-10-21）
    const role = roleName || GameData.SCRIPT_MAP[scriptId]?.roles[roleIndex]?.name;
    return role ? `${roleType}「${role}」` : roleType;
}

/** 《剧名》+ 定位「角色」拼一行（奖项行用，缺任一半都还读得通） */
function workLabel(workName: string, roleLabel: string): string {
    return [workName ? `《${workName}》` : '', roleLabel].filter(Boolean).join(' ');
}

function section(title: string, rows: HTMLElement[], emptyText: string): HTMLElement {
    const box = createDiv('career-sec');
    box.appendChild(createSpan('career-sec-title', title));
    if (!rows.length) {
        box.appendChild(createDiv('career-empty', [emptyText]));
        return box;
    }
    rows.forEach(r => box.appendChild(r));
    return box;
}

function chip(text: string): HTMLElement {
    return createSpan('career-chip', text);
}

/** 获奖/入围小旗：获奖走薄荷实底（与站内正向读数同档），入围只留描边 */
function flag(text: string, win: boolean): HTMLElement {
    return createSpan(`career-flag${win ? ' win' : ''}`, text);
}
